import { Component, ElementRef, Inject, OnInit, ViewChild } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { CommentsService } from 'src/app/service/comments.service';
import { checkDynamicContent, checkIfFieldDisabled, removeUndefinedValuesFromObject } from 'na-lib';
import { UploadService } from 'src/app/service/upload.service';
import { TranslateService } from '@ngx-translate/core';
import { urlInvalidCharactersValidator } from 'src/app/validators/formValidators.dt';
import { MediaDialogHandlerComponent } from 'src/app/media/media-dialog-handler/media-dialog-handler.component';
import { ImageCompressionService } from 'src/app/service/image-compression.service';

type LinkChip =
  | { kind: 'child'; id: string; label: string }
  | { kind: 'anchor'; id: string; label: string };

interface AnchorOption {
  id: string;
  label?: string;
  name?: string;
}
interface ChildPageOption {
  id: string;
  name?: string;
}

@Component({
    selector: 'app-media-details-dialog',
    templateUrl: './media-details-dialog.component.html',
    styleUrls: ['./media-details-dialog.component.css'],
    standalone: false
})
export class MediaDetailsDialogComponent implements OnInit{
  oldData:any
  mediaDetailsData:any
  mediaDetailsForm!: FormGroup
  billboardImage=false
  billboardVideo=false
  text=false
  image=false
  callToAction=false
  video=false
  images=false
  widgetType:any
  imagesHorizontal=false
  imageBlocks=false
  splashVideo=false
  newDocument:any
  passId:any
  linkUrlTransform:any
  widgetNumber:any
  urlRegex = /^(?:\$\{[A-Za-z0-9._:-]+\}|#\S+|child:[^\s]+|dpp:\/\/main|(?:https?:\/\/|context:\/\/|pass:\/\/|content:\/\/|task:\/\/|tridy:\/\/)\S+)$/;
  urlRegexDoc = /^(?:\$\{[^}]+\}|pass:\/\/[\w.-]+(?:\.[\w.-]+)+[\w\-._~:/?#[\]@!$&'()*+,;=.]+)$/;
  urlRegexDynamic = /^\$\{[A-Za-z0-9._:-]+\}$/;

  maxSizeInBytes=2 * 1024 * 1024 // 2 MB size limit for compression
  maxAllowedSizeInBytes=20 * 1024 * 1024 // 20 MB size limit for direct upload
  @ViewChild('urlFileInput') fileInput: ElementRef<HTMLInputElement>;

  anchorOptions: AnchorOption[] = [];
  childPages: ChildPageOption[] = [];
  linkChip: LinkChip | null = null;

  constructor(public dialogRef: MatDialogRef<MediaDetailsDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: any,
    private commentService: CommentsService,
    private fb: FormBuilder,
    private translate:TranslateService,
    private uploadService: UploadService,
    private imageCompressionService: ImageCompressionService,
    private dialog: MatDialog){
      dialogRef.disableClose=true
    }

  ngOnInit():void{
    this.oldData = this.data.values
    this.widgetType = this.data['type']
    this.mediaDetailsData = this.data.values
    this.passId = this.data['passId']
    this.widgetNumber= this.data['widgetNumber']

    if(this.data.translationMode){
      let translateData = this.data.translateData
      const {fields, originalLanguageData, defaultLanguageWidgetData, valuesTranslated, selectedLanguage, values} = translateData
      this.initTranslationData(this.data.values, originalLanguageData, fields)
    }else
    this.initOriginalData()
    
    switch(this.widgetType){
      case 'billboardImage': {
        this.billboardImage=true
        break;
      }

      case 'billboardVideo': {
        this.billboardVideo=true
        break;
      }

      case 'text': {
        this.text=true
        if(this.mediaDetailsData.image){
          const image = this.mediaDetailsData.image
          if(image.url)
            this.mediaDetailsForm.patchValue({url: image.url})

          if(image.linkURL)
            this.mediaDetailsForm.patchValue({linkURL: image.linkURL})

          if(image.changingThisBreaksApplicationSecurity)
            this.mediaDetailsForm.patchValue({url: image})
        }

        break;
      }

      case 'image': {
        this.image=true
        break;
      }

      case 'callToAction': {
        this.callToAction = true
        break;
      }
      case 'video': {
        this.video = true
        break;
      }

      case 'imagesHorizontal': {
        this.imagesHorizontal = true
        break;
      }

      case 'imageBlocks': {
        this.imageBlocks = true
        break;
      }
      case 'splashVideo': {
        this.splashVideo = true
        break;
      }

    }

    const extra = this.data as any;
    this.anchorOptions = extra.anchorOptions || [];
    this.childPages   = extra.childPages || [];
    const ctrl = this.mediaDetailsForm?.get('linkURL');

    if (ctrl) {
      this.linkChip = this.makeLinkChip(ctrl.value);
      ctrl.valueChanges.subscribe(v => {
        this.linkChip = this.makeLinkChip(v);
      });
    }
  }

  initOriginalData(){
    const { url, fallbackUrl,fallbackURL, title, copyright, linkURL, description, visibility, type, size, altTitle, id} = this.mediaDetailsData

    this.mediaDetailsForm = this.fb.group({
      id:[{value:  id ? id : undefined, disabled:false} ],
      url:[{value: url ? url : undefined, disabled: false},[Validators.required, Validators.pattern(this.urlRegex), urlInvalidCharactersValidator()]],
      fallbackUrl:[{value: fallbackURL ? fallbackURL : (fallbackUrl ? fallbackUrl : undefined), disabled:false} ,[ Validators.pattern(this.urlRegex), urlInvalidCharactersValidator()]],
      title:[{value: title ? title : undefined, disabled:false} , [Validators.maxLength(40)]],
      copyright:[{value: copyright ? copyright : undefined, disabled:false} ],
      linkURL:[ {value: linkURL ? linkURL : undefined, disabled:false} ,[ Validators.pattern(this.urlRegex), urlInvalidCharactersValidator()]],
      description:[{value: description ? description : undefined, disabled:false} ],
      visibility:[{value: visibility ? visibility : 'visible', disabled:false} ],
      size:[{value:  size ? size : undefined, disabled:false} ],
      altTitle:[{value: altTitle ? altTitle : undefined, disabled:false} ]
    })

    this.mediaDetailsForm.get("linkURL")?.valueChanges.subscribe( (newurl) => {
      this.setFieldValidator('linkURL', newurl)
    })

    this.mediaDetailsForm.get("url")?.valueChanges.subscribe( (newurl) => {
      this.setFieldValidator('url', newurl)
    })
  }

  initTranslationData(data, originalLanguageData, fields){
    const { url, fallbackUrl,fallbackURL, title, copyright, linkURL, description, visibility, type, size, altTitle, id} = data

    this.mediaDetailsForm = this.fb.group({
      id:[{value: id ? id : undefined, disabled: checkIfFieldDisabled('images.id', fields, originalLanguageData)} ],
      url:[{value: url ? url : undefined, disabled: checkIfFieldDisabled('images.url', fields, originalLanguageData)},[ Validators.pattern(this.urlRegex), urlInvalidCharactersValidator()]],
      fallbackUrl:[{value: fallbackURL ? fallbackURL : (fallbackUrl ? fallbackUrl : undefined), disabled:checkIfFieldDisabled('images.fallbackUrl', fields, originalLanguageData)} ,[ Validators.pattern(this.urlRegex), urlInvalidCharactersValidator()]],
      title:[{value: title ? title : undefined, disabled: checkIfFieldDisabled('images.title', fields, originalLanguageData)} , [Validators.maxLength(40)]],
      copyright:[{value: copyright ? copyright : undefined, disabled:checkIfFieldDisabled('images.copyright', fields, originalLanguageData)} ],
      linkURL:[ {value: linkURL ? linkURL : undefined, disabled: checkIfFieldDisabled('images.linkURL', fields, originalLanguageData)} ,[ Validators.pattern(this.urlRegex), urlInvalidCharactersValidator()]],
      description:[{value: description ? description : undefined, disabled: checkIfFieldDisabled('images.description', fields, originalLanguageData)} ],
      visibility:[{value: visibility ? visibility : 'visible', disabled: checkIfFieldDisabled('images.visibility', fields, originalLanguageData)} ],
      size:[{value:  size ? size : undefined, disabled: checkIfFieldDisabled('images.size', fields, originalLanguageData)} ],
      altTitle:[{value: altTitle ? altTitle : undefined, disabled: checkIfFieldDisabled('images.altTitle', fields, originalLanguageData)} ]
    })

    this.mediaDetailsForm.get("linkURL")?.valueChanges.subscribe( (newurl) => {
      this.setFieldValidator('linkURL', newurl)
    })

    this.mediaDetailsForm.get("url")?.valueChanges.subscribe( (newurl) => {
      this.setFieldValidator('url', newurl)
    })

  }

  checkPattern_url(field){
    const fiedlValue:any = this.mediaDetailsForm.get(field)
    if(fiedlValue){
      try{
        const imgUrl = new URL(fiedlValue)

        if(imgUrl.protocol === "http:" || imgUrl.protocol === "https:" || imgUrl.protocol === "tridy:" || imgUrl.protocol === "context:" )
          this.mediaDetailsForm.get(field)?.setValidators([Validators.pattern(this.urlRegex), urlInvalidCharactersValidator()]);

        if(imgUrl.protocol === "pass:" )
          this.mediaDetailsForm.get(field)?.setValidators([Validators.pattern(this.urlRegexDoc), urlInvalidCharactersValidator()]);

        this.mediaDetailsForm.updateValueAndValidity()
      }catch(e){}
    }
  }

  setFieldValidator(field, value) {
    if (!value) return;

    if (value.match(/\${(.+)\}/)){
      this.mediaDetailsForm.get(field)?.setValidators([ Validators.pattern(this.urlRegexDynamic)]);
      this.mediaDetailsForm.get(field)?.updateValueAndValidity({ emitEvent: false });
    } else {
      this.checkPattern_url(field)
    }
  }

  buildAnchorUrl(anchor: { id: string }): string {
    if (!anchor || !anchor.id) {
      console.warn('buildAnchorUrl: invalid anchor', anchor);
      return '';
    }
    return `#${anchor.id}`;
  }

  buildChildPageUrl(child: { id: string }): string {
    if (!child || !child.id) {
      console.warn('buildChildPageUrl: invalid child', child);
      return '';
    }
    return child.id === 'main' ? 'dpp://main' : `child:${child.id}`;
  }

  setLinkUrl(value: string): void {
    const controlName = 'linkURL';
    this.mediaDetailsForm.patchValue({
      [controlName]: value
    });

    const ctrl = this.mediaDetailsForm.get(controlName);
    if (ctrl) {
      ctrl.markAsDirty();
      ctrl.markAsTouched();
      ctrl.updateValueAndValidity();
    }
  }

    private makeLinkChip(value: string | null | undefined): LinkChip | null {
    if (!value) return null;
    // dpp://main
    if (value === 'dpp://main') {
      const match = this.childPages?.find(x => x.id === 'main');
      return { kind: 'child', id: 'main', label: match?.name || 'Main' };
    }
    // child:<id>
    if (value.startsWith('child:')) {
      const id = value.slice('child:'.length).trim();
      if (!id) return null;
      const match = this.childPages?.find(x => x.id === id);
      return {
        kind: 'child',
        id,
        label: match?.name || id,
      };
    }
    // #<id>
    if (value.startsWith('#')) {
      const id = value.slice(1).trim();
      if (!id) return null;
      const match = this.anchorOptions?.find(x => x.id === id);
      return {
        kind: 'anchor',
        id,
        label: match?.label || match?.name || id,
      };
    }
    // normal url -> no chip
    return null;
  }

  clearLinkChip(): void {
    this.setLinkUrl('');
  }

  readDocument(event: any): void {
    if (event.target.files && event.target.files[0]) {
      const file = event.target.files[0];
      const maxSizeInBytes = 10 * 1024 * 1024; // 10 MB size limit
      
      // Define allowed file types
      const allowedTypes = [
        'application/pdf',
        'image/jpeg',
        'image/png',
        'image/webp',
        'application/msword',
        'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
        'application/vnd.ms-excel',
        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        'text/plain',
        'text/csv',
        'text/vcard',
        'application/rtf'
      ];
  
      // Validate file type
      if (!allowedTypes.includes(file.type)) {
        const message = this.translate.instant("SnackBarConstants.INVALID_FILE_FORMAT");
        this.commentService.addSnackBar.emit(message);
        return;
      }
      
      // Validate file size
      if (file.size > maxSizeInBytes) {
        const message = this.translate.instant("SnackBarConstants.FILE_TOO_LARGE");
        this.commentService.addSnackBar.emit(message);
        return;
      }
      
      // Proceed with reading and uploading the file
      const reader = new FileReader();
      this.newDocument = file;
      reader.readAsDataURL(file);
      reader.onload = async (e) => {
        if (e.target) {
          const urlNew = e.target.result as string;
          try {
            const uploadURL = await this.uploadService.uploadDocument(this.passId, this.newDocument, this.widgetNumber);
            if (uploadURL) {
              this.mediaDetailsForm.get("linkURL")?.setValidators([Validators.required, Validators.pattern(this.urlRegexDoc), urlInvalidCharactersValidator()]);
              this.linkUrlTransform = uploadURL;
              this.mediaDetailsForm.patchValue({ linkURL: this.linkUrlTransform });
            }
          } catch (error) {
            console.error(error);
            const message = this.translate.instant("SnackBarConstants.UPLOAD_IMG_FAILED");
            this.commentService.addSnackBar.emit(message);
          }
        }
      };
    }
  }

  browserFromMediaFolder(){
    let dialogRef= this.dialog.open(MediaDialogHandlerComponent, {
      data: {
        supportedFiles: ['image/jpeg', 'image/png', 'image/webp'],
      },
    });
  
    dialogRef.afterClosed().subscribe(result => {
      if(result[0]){
        const url = result[1]
        const supportUrl = result[2]
        this.mediaDetailsForm.patchValue({url})
      }
    });
  }

  triggerFileInput(): void {
    this.fileInput.nativeElement.click();
  }

  async compressFileAndUpload(file, index){
    const fileToUpload = file;
    const maxSizeInBytes = this.maxSizeInBytes
    const maxAllowedSizeInBytes = this.maxAllowedSizeInBytes
  
    if (maxAllowedSizeInBytes && (fileToUpload.size > maxAllowedSizeInBytes)) {// File is too large for both upload and compression
      const message = this.translate.instant("SnackBarConstants.IMAGE_FILE_TOO_LARGE");
      this.commentService.addSnackBar.emit(message);
      return;
    }
  
    if (maxSizeInBytes && (fileToUpload.size > maxSizeInBytes)) {// File is between 2 MB and 20 MB, attempt compression
      try {
        const compressedImage = await this.imageCompressionService.compressImage(fileToUpload, maxSizeInBytes);
        if (!compressedImage) {
          const message = this.translate.instant("SnackBarConstants.IMAGE_TOO_LARGE");
          this.commentService.addSnackBar.emit(message);
          return;
        }else{
          await this.uploadFile(compressedImage, index)
        }
      } catch (error) {
        console.error("Compression error:", error);
        const message = this.translate.instant("SnackBarConstants.IMAGE_COMPRESSION_FAILED");
        this.commentService.addSnackBar.emit(message);
        return;
      }
    } else {// File is less than or equal to 2 MB, no compression needed
      await this.uploadFile(fileToUpload, index)
    }
  }

  readURL(event: any): void {
    if (event.target.files && event.target.files[0]) {
      const file = event.target.files[0];
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = async (e) => {
        if (e.target) {
          const urlNew = e.target.result as string;
          if (urlNew) {
            this.compressFileAndUpload(file, this.widgetNumber)
          }
        }
      };
    }
  }

  async uploadFile(file, index){
    try{
      const passId = this.passId
      const widgetNumber = this.widgetNumber
      this.commentService.progressSpin.emit(true)
      const uploadURL = await this.uploadService.uploadNewImages(passId, file, index, widgetNumber);
      if (uploadURL) {
        this.mediaDetailsForm.patchValue({url:uploadURL})
      }

      setTimeout(() =>{
        this.commentService.progressSpin.emit(false)
        const message = this.translate.instant("SnackBarConstants.UPLOAD_FILE_OK")
        this.commentService.addSnackBar.emit(message)
      }, 800);
    }catch(error){
      console.log(error)
      const message = this.translate.instant("SnackBarConstants.UPLOAD_IMG_FAILED")
      this.commentService.addSnackBar.emit(message)
    }
  }
  
  saveChanges(){
    const data = this.mediaDetailsForm.value

    if(data.linkURL=='https://'){
      data.linkURL = undefined
    }

    if(data.url && checkDynamicContent(data.url))
      delete data.size

    removeUndefinedValuesFromObject(data)

    this.dialogRef.close([data, true])
  }
  cancelChanges(){
    this.dialogRef.close([this.oldData, false])

  }
}